import { InvitedUser, User, UserWithRole } from "@/models/users/User";
import { CrudServiceType, crudService } from "./crudService";
import { RestClient } from "./restClient";
import RequestMethod from "@/models/client/RequestMethod";
import RestResponse from "@/models/client/RestResponse";
import { Pagination } from "@/models/base/Pagination";
import { TenantRole } from "@/models/tenant/tenant";
import {
  ConnectionResponse,
  DeleteInvitedUserRequest,
  InviteUserRequest,
} from "@/models/user-management/UserManagement";

/** Sort keys the users-with-roles endpoint understands. */
export type UserSortBy = "name" | "email" | "createdAt";

/** Sort keys the invited-users endpoint understands. */
export type InvitedUserSortBy =
  | "email"
  | "inviter"
  | "createdAt"
  | "expiresAt";

export type UserManagementServiceType = CrudServiceType<User> & {
  /** `pageNumber` is one-based, matching the API. */
  getUsersWithRoles(
    pageNumber: number,
    pageSize: number,
    search?: string,
    sortBy?: UserSortBy,
    sortAscending?: boolean
  ): RestResponse<Pagination<UserWithRole>>;
  updateUserRoles(
    userId: string,
    tenantId: string,
    roles: TenantRole[]
  ): RestResponse<boolean>;
  /** `pageNumber` is one-based, matching the API. */
  getInvitedUsers(
    pageNumber: number,
    pageSize: number,
    sortBy?: InvitedUserSortBy,
    sortAscending?: boolean
  ): RestResponse<Pagination<InvitedUser>>;
  inviteUser(request: InviteUserRequest): RestResponse<void>;
  getConnectionDetails(): RestResponse<ConnectionResponse[]>;
  deleteInvitedUser(request: DeleteInvitedUserRequest): RestResponse<void>;
  deleteUser(userId: string): RestResponse<void>;
};

const userManagementServices = (
  client: RestClient,
  path: string
): UserManagementServiceType => ({
  ...crudService<User>(client, path),
  getUsersWithRoles: async (
    pageNumber: number,
    pageSize: number,
    search?: string,
    sortBy: UserSortBy = "name",
    sortAscending: boolean = true
  ) => {
    const searchParam = search ? `&search=${encodeURIComponent(search)}` : "";
    const response = await client.fetch<Pagination<UserWithRole>>(
      RequestMethod.GET,
      `${path}/users-with-roles?pageNumber=${pageNumber}&pageSize=${pageSize}${searchParam}` +
        `&sortBy=${sortBy}&sortAscending=${sortAscending}`
    );
    return response;
  },
  updateUserRoles: async (
    userId: string,
    tenantId: string,
    roles: TenantRole[]
  ) => {
    const response = await client.fetch<boolean>(
      RequestMethod.PUT,
      `${path}/update-roles/`,
      {
        userId,
        tenantId,
        roles,
      }
    );
    return response;
  },
  getInvitedUsers: async (
    pageNumber: number,
    pageSize: number,
    sortBy: InvitedUserSortBy = "email",
    sortAscending: boolean = true
  ) => {
    const response = await client.fetch<Pagination<InvitedUser>>(
      RequestMethod.GET,
      `${path}/invited-users?pageNumber=${pageNumber}&pageSize=${pageSize}` +
        `&sortBy=${sortBy}&sortAscending=${sortAscending}`
    );
    return response;
  },
  getConnectionDetails: async () => {
    const response = await client.fetch<ConnectionResponse[]>(
      RequestMethod.GET,
      `${path}/connections`
    );
    return response;
  },
  inviteUser: async (request: InviteUserRequest) => {
    const response = await client.fetch<void>(
      RequestMethod.POST,
      `${path}/invitation`,
      request
    );
    return response;
  },
  deleteInvitedUser: async (request: DeleteInvitedUserRequest) => {
    const response = await client.fetch<void>(
      RequestMethod.DELETE,
      `${path}/invited-users`,
      request
    );
    return response;
  },
  deleteUser: async (userId: string) => {
    // Note: Delete endpoint is on UserController at /api/user/{id}
    const response = await client.fetch<void>(
      RequestMethod.DELETE,
      `/api/user/${userId}`
    );
    return response;
  },
});

export default userManagementServices;
